import {
  type LcmStudioChannel,
  LcmStudioChannels,
} from "@hightouch/lib/llm/lifecycle-studio/types";
import type { Folder as FolderTreeNode } from "src/components/folders/types";
import { flattenFolders, nestFolders } from "src/components/folders/utils";
import type {
  SavedMessagesBoolExp,
  SavedMessagesIndexQuery,
  SavedMessagesQuery,
} from "src/graphql";
import { MessageEditorOrigins } from "src/pages/lifecycle-studio/utils/message-editor-urls";

export type SavedMessageRow = NonNullable<
  SavedMessagesQuery["saved_messages"]
>[number];

export const LCM_MESSAGES_FOLDER_TYPE = "lifecycle-messages";

export const PAGE_SIZE = 20;

export const NO_FOLDER_VALUE = "__none__";

export type ChannelTab = "all" | LcmStudioChannel;

export const CHANNEL_TABS: { key: ChannelTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "email", label: "Email" },
  { key: "sms", label: "SMS" },
  { key: "push", label: "Push" },
];

export type FolderScope = "all" | "none" | { folderId: string };

export type MessageFolder = {
  id: string;
  name: string;
  parent_id: string | null;
};

export type MessageFolderNode = FolderTreeNode;

export function buildFolderTree(folders: MessageFolder[]): MessageFolderNode[] {
  return nestFolders(
    folders.map((f) => ({
      id: f.id,
      name: f.name,
      parent_id: f.parent_id,
      type: LCM_MESSAGES_FOLDER_TYPE,
      count: 0,
    })),
  );
}

export function buildDescendantsMap(
  tree: MessageFolderNode[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const visit = (node: MessageFolderNode) => {
    map.set(
      node.id,
      new Set<string>([
        node.id,
        ...flattenFolders(node.children).map((c) => c.id),
      ]),
    );
    node.children.forEach(visit);
  };
  tree.forEach(visit);
  return map;
}

export function flattenFolderTree(
  tree: MessageFolderNode[],
): MessageFolderNode[] {
  return flattenFolders(tree);
}

/**
 * Ordered ancestry (root → leaf) of the folder identified by `folderId`, walking
 * up the `parent_id` chain. Returns [] when the message is unfiled or the folder
 * (or an ancestor) can't be resolved. Guards against cyclic parent links so a
 * corrupted chain can't spin forever.
 */
export function buildFolderAncestry(
  folderId: string | null | undefined,
  folders: MessageFolder[],
): MessageFolder[] {
  if (!folderId) return [];
  const byId = new Map(folders.map((f) => [f.id, f]));
  const chain: MessageFolder[] = [];
  const seen = new Set<string>();
  let current = byId.get(folderId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    chain.unshift(current);
    current = current.parent_id ? byId.get(current.parent_id) : undefined;
  }
  return chain;
}

export const folderPathLabel = (folder: { path: string }): string =>
  folder.path.replaceAll("/", " / ");

export type CurationFilter = "all" | "favorited" | "promoted";

export enum SavedMessageSortKeys {
  Name = "name",
  UpdatedAt = "updated_at",
}

export type FacetOption = { id: string; label: string };

export function toFacetOptions<T>(
  rows: T[] | undefined,
  getOption: (row: T) => {
    // bigint columns are typed as string but arrive as numbers at runtime, so
    // accept both — the id is stringified below regardless.
    id: string | number | null | undefined;
    label: string | null | undefined;
  },
): FacetOption[] {
  return (rows ?? []).flatMap((row) => {
    const { id, label } = getOption(row);
    // `bigint` columns (created_by / edited_by) arrive as JS numbers at runtime
    // despite being typed as string, while the URL-backed selected ids are
    // strings. Normalize option ids to strings so equality checks against the
    // selection line up (uuid campaign ids are already strings).
    return id != null && label ? [{ id: String(id), label }] : [];
  });
}

export function buildSearchFilter(
  search: string | undefined,
): SavedMessagesBoolExp {
  const trimmed = search?.trim();
  if (!trimmed) return {};
  const like = `%${trimmed}%`;
  return {
    _or: [
      { title: { _ilike: like } },
      { subject: { _ilike: like } },
      { name: { _ilike: like } },
    ],
  };
}

export function buildSavedMessagesFilter({
  searchFilter,
  channelTab,
  folderScope,
  descendantsById,
  curationFilter,
  campaignIds,
  createdByIds,
  editedByIds,
}: {
  searchFilter: SavedMessagesBoolExp;
  channelTab: ChannelTab;
  folderScope: FolderScope;
  descendantsById: Map<string, Set<string>>;
  curationFilter: CurationFilter;
  campaignIds: string[];
  createdByIds: string[];
  editedByIds: string[];
}): SavedMessagesBoolExp {
  const and: SavedMessagesBoolExp[] = [searchFilter];

  if (channelTab !== "all") and.push({ channel: { _eq: channelTab } });

  if (folderScope === "none") and.push({ folder_id: { _is_null: true } });
  else if (typeof folderScope === "object") {
    // Selecting a folder includes its whole subtree (self + descendants).
    const subtreeIds = Array.from(
      descendantsById.get(folderScope.folderId) ??
        new Set([folderScope.folderId]),
    );
    and.push({ folder_id: { _in: subtreeIds } });
  }

  // `my_favorites` is Hasura-prefiltered to the current user (see the
  // saved_messages permission), so an existence check is "favorited by me" — no
  // need to thread the user id (which risked an empty result before it loaded).
  if (curationFilter === "favorited") and.push({ my_favorites: {} });
  if (curationFilter === "promoted") and.push({ promoted: { _eq: true } });
  if (campaignIds.length) and.push({ campaign_id: { _in: campaignIds } });
  if (createdByIds.length) and.push({ created_by: { _in: createdByIds } });
  if (editedByIds.length) and.push({ edited_by: { _in: editedByIds } });

  return { _and: and };
}

// Project-sourced messages open inside the project chrome (candidates editor)
// so the path back to the project/plan stays in view; standalone and legacy
// messages open in the top-level content-library editor. Returns null when the
// row lacks the id needed to build a valid edit href.
export function buildMessageEditHref(row: SavedMessageRow): string | null {
  // The candidates-nested editor resolves its back arrow from `?from=`; stamp
  // the library as the origin so Back returns here instead of the candidates
  // list. The top-level editor's back target is already the library.
  const isProjectEditor = row.source_kind === "project" && row.campaign_id;
  const base = isProjectEditor
    ? `/lifecycle-studio/projects/${row.campaign_id}/candidates`
    : "/lifecycle-studio";
  const from = isProjectEditor ? `&from=${MessageEditorOrigins.Messages}` : "";

  if (row.channel === LcmStudioChannels.Email) {
    // Email rows open the message editor + embedded chat.
    const email = row.content_email;
    if (!email) return null;
    const sessionId =
      email.llm_chat_session_id ?? row.created_by_llm_chat_session_id ?? "";
    return `${base}/messages/${email.id}/edit?sessionId=${sessionId}${from}`;
  }

  // SMS/push rows are copy artifacts; the copy editor keys off the
  // campaign_artifact id, which is the saved-messages artifact_id.
  if (!row.artifact_id) return null;
  const sessionId = row.created_by_llm_chat_session_id ?? "";
  return `${base}/messages/copy/${row.artifact_id}/edit?sessionId=${sessionId}${from}`;
}

export function getScopeTitle(
  folderScope: FolderScope,
  flatFolders: MessageFolderNode[],
): string {
  if (folderScope === "all") return "All messages";
  if (folderScope === "none") return "No folder";
  return (
    flatFolders.find((f) => f.id === folderScope.folderId)?.name ?? "Messages"
  );
}

export type IndexRow = SavedMessagesIndexQuery["saved_messages"][number];

export type CountsShape = {
  channel: Record<ChannelTab, number>;
  curationAll: number;
  favorites: number;
  promoted: number;
  folderAll: number;
  folderNone: number;
  byFolder: Record<string, number>;
};

export const isFavorited = (row: {
  my_favorites?: Array<unknown> | null;
}): boolean => (row.my_favorites?.length ?? 0) > 0;

/**
 * Foldering requires workspace visibility: the message must be standalone or
 * promoted (or already filed). A campaign-backed message that a user has only
 * favorited — not promoted, not standalone, and not yet in a folder — has no
 * workspace-visible artifact to file, so it can't be moved into a folder
 */
export function isMessageFolderable(row: {
  promoted?: boolean | null;
  source_kind?: string | null;
  folder_id?: string | null;
  my_favorites?: Array<unknown> | null;
}): boolean {
  const favoritedOnly =
    isFavorited(row) &&
    !row.promoted &&
    row.source_kind !== "standalone" &&
    row.folder_id == null;
  return !favoritedOnly;
}

const matchesFacets = (
  row: IndexRow,
  campaignIds: string[],
  createdByIds: string[],
  editedByIds: string[],
): boolean =>
  // created_by / edited_by are bigint columns that arrive as numbers at runtime,
  // so stringify before comparing to the string selection ids (see toFacetOptions).
  (!campaignIds.length ||
    (row.campaign_id != null &&
      campaignIds.includes(String(row.campaign_id)))) &&
  (!createdByIds.length ||
    (row.created_by != null &&
      createdByIds.includes(String(row.created_by)))) &&
  (!editedByIds.length ||
    (row.edited_by != null && editedByIds.includes(String(row.edited_by))));

const matchesFolder = (
  row: IndexRow,
  scope: FolderScope,
  descendantsById: Map<string, Set<string>>,
): boolean =>
  scope === "all"
    ? true
    : scope === "none"
      ? row.folder_id == null
      : row.folder_id != null &&
        (descendantsById.get(scope.folderId)?.has(row.folder_id) ?? false);

export function computeCounts({
  indexRows,
  folders,
  descendantsById,
  folderScope,
  channelTab,
  curationFilter,
  campaignIds,
  createdByIds,
  editedByIds,
}: {
  indexRows: IndexRow[];
  folders: Array<{ id: string }>;
  descendantsById: Map<string, Set<string>>;
  folderScope: FolderScope;
  channelTab: ChannelTab;
  curationFilter: CurationFilter;
  campaignIds: string[];
  createdByIds: string[];
  editedByIds: string[];
}): CountsShape {
  const facetMatch = (r: IndexRow) =>
    matchesFacets(r, campaignIds, createdByIds, editedByIds);

  const matchesCuration = (r: IndexRow) =>
    curationFilter === "all"
      ? true
      : curationFilter === "favorited"
        ? isFavorited(r)
        : Boolean(r.promoted);

  const inChannel = (r: IndexRow) =>
    channelTab === "all" || r.channel === channelTab;

  const channelCount = (tab: ChannelTab) =>
    indexRows.filter(
      (r) =>
        matchesFolder(r, folderScope, descendantsById) &&
        matchesCuration(r) &&
        facetMatch(r) &&
        (tab === "all" || r.channel === tab),
    ).length;

  const folderCount = (scope: FolderScope) =>
    indexRows.filter(
      (r) =>
        inChannel(r) &&
        matchesCuration(r) &&
        facetMatch(r) &&
        matchesFolder(r, scope, descendantsById),
    ).length;

  const inCurationBase = (r: IndexRow) =>
    inChannel(r) &&
    matchesFolder(r, folderScope, descendantsById) &&
    facetMatch(r);

  return {
    channel: {
      all: channelCount("all"),
      email: channelCount("email"),
      sms: channelCount("sms"),
      push: channelCount("push"),
    },
    curationAll: indexRows.filter(inCurationBase).length,
    favorites: indexRows.filter((r) => inCurationBase(r) && isFavorited(r))
      .length,
    promoted: indexRows.filter((r) => inCurationBase(r) && r.promoted).length,
    folderAll: folderCount("all"),
    folderNone: folderCount("none"),
    byFolder: Object.fromEntries(
      folders.map((f) => [f.id, folderCount({ folderId: f.id })]),
    ) as Record<string, number>,
  };
}
